import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { BRAND } from '../config';

const G = '#00C060';
const BG = '#f0f7f2';
const CARD = '#ffffff';
const BORDER = 'rgba(0, 192, 96, 0.4)';
const SHADOW = '0 2px 12px rgba(0,0,0,0.08)';
const inputStyle = { background: '#f8faf8', borderColor: 'rgba(0,166,81,0.35)', color: '#1a3322' };

const DailyQuotas = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [quotasData, setQuotasData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quotaLoading, setQuotaLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showEditModal, setShowEditModal] = useState(false);
  const [editQuota, setEditQuota] = useState({ pdv_id: null, prize_config_id: null, date: '', prize_label: '', max_quantity: 0, used_quantity: 0 });

  useEffect(() => {
    if (user?.role !== 'admin' && user?.role !== 'super_admin') { navigate('/dashboard'); return; }
    api.get('/admin/sessions', { params: { brand: BRAND } })
      .then(r => setSessions(r.data))
      .catch(() => setMessage({ type: 'danger', text: 'Erreur chargement sessions' }))
      .finally(() => setLoading(false));
  }, [user, navigate]);

  const loadQuotas = async (sessionId) => {
    if (!sessionId) { setQuotasData(null); return; }
    setQuotaLoading(true);
    try {
      const response = await api.get(`/admin/daily-quotas/${sessionId}`, { params: { brand: BRAND } });
      setQuotasData(response.data);
    } catch (error) {
      setMessage({ type: 'danger', text: 'Erreur lors du chargement des quotas' });
    } finally {
      setQuotaLoading(false);
    }
  };

  const handleSessionChange = (e) => {
    const sessionId = parseInt(e.target.value);
    const session = sessions.find(s => s.id === sessionId);
    setSelectedSession(session || null);
    loadQuotas(sessionId || null);
  };

  const handleEditQuota = (quota) => {
    setEditQuota({ pdv_id: quotasData.pdv_id, prize_config_id: quota.prize_config_id, date: quota.date, prize_label: quota.prize_label, max_quantity: quota.max_quantity, used_quantity: quota.used_quantity });
    setShowEditModal(true);
  };

  const handleSaveQuota = async () => {
    try {
      await api.put(`/admin/daily-quota/${editQuota.pdv_id}/${editQuota.prize_config_id}`, {
        quota_date: editQuota.date, max_quantity: editQuota.max_quantity, used_quantity: editQuota.used_quantity
      });
      setMessage({ type: 'success', text: 'Quota mis à jour' });
      setShowEditModal(false);
      loadQuotas(selectedSession.id);
    } catch (error) {
      setMessage({ type: 'danger', text: error.response?.data?.detail || 'Erreur lors de la mise à jour' });
    }
  };

  const getBadge = (used, max) => {
    const pct = (used / max) * 100;
    const cfg = pct >= 100 ? { label: 'Épuisé', color: '#e53935', bg: 'rgba(229,57,53,0.15)' }
      : pct >= 80 ? { label: 'Critique', color: '#f57c00', bg: 'rgba(245,124,0,0.15)' }
      : pct >= 50 ? { label: 'Moyen', color: '#1890ff', bg: 'rgba(24,144,255,0.15)' }
      : { label: 'OK', color: '#00A651', bg: 'rgba(0,192,96,0.15)' };
    return <span style={{ padding: '3px 12px', borderRadius: '12px', fontSize: '11px', fontWeight: 700, color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.color}40` }}>{cfg.label}</span>;
  };

  const getProgress = (used, max) => {
    const pct = Math.min((used / max) * 100, 100);
    const color = pct >= 100 ? '#e53935' : pct >= 80 ? '#f57c00' : pct >= 50 ? '#1890ff' : G;
    return (
      <div style={{ height: '7px', background: 'rgba(255,255,255,0.15)', borderRadius: '4px', overflow: 'hidden', minWidth: '100px' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: '4px', transition: 'width 0.3s' }}></div>
      </div>
    );
  };

  if (loading) return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: BG }}>
      <div className="spinner-border" style={{ color: G }} role="status"></div>
    </div>
  );

  return (
    <div className="min-vh-100" style={{ background: BG }}>
      <nav className="navbar navbar-dark sticky-top" style={{ background: '#0a1f0c', padding: '10px 0', borderBottom: `2px solid rgba(0,192,96,0.4)` }}>
        <div className="container d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-3">
            <img src="/logo-white.png" alt="RNP" style={{ width: '46px', height: 'auto' }} />
            <div>
              <div className="fw-bold text-white" style={{ fontSize: '16px' }}>Reaktor Nature Power</div>
              <div style={{ fontSize: '11px', color: G }}>Quotas Journaliers</div>
            </div>
          </div>
          <button onClick={() => navigate('/dashboard')} className="btn btn-sm"
            style={{ background: 'rgba(0,192,96,0.2)', border: `1px solid rgba(0,192,96,0.5)`, color: '#fff', fontSize: '13px' }}>
            <i className="fas fa-arrow-left me-2"></i>Dashboard
          </button>
        </div>
      </nav>

      <div className="container py-4">
        {message.text && (
          <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show mb-3`} role="alert">
            {message.text}
            <button type="button" className="btn-close btn-close-white" onClick={() => setMessage({ type: '', text: '' })}></button>
          </div>
        )}

        <div className="mb-4">
          <h2 className="fw-bold mb-1" style={{ color: '#1a3322', fontSize: '26px' }}>
            <i className="fas fa-calendar-day me-2" style={{ color: G }}></i>Quotas Journaliers
          </h2>
          <p style={{ color: 'rgba(0,0,0,0.5)', margin: 0 }}>{sessions.length} session(s) configurée(s)</p>
        </div>

        <div style={{ background: CARD, borderRadius: '14px', border: `1px solid ${BORDER}`, boxShadow: SHADOW, padding: '20px', marginBottom: '24px' }}>
          <label style={{ color: '#1a3322', fontWeight: 700, fontSize: '13px', display: 'block', marginBottom: '8px' }}>
            <i className="fas fa-calendar-alt me-2" style={{ color: G }}></i>Sélectionner une session
          </label>
          <select className="form-select" value={selectedSession?.id || ''} onChange={handleSessionChange} style={inputStyle}>
            <option value="">-- Choisir une session --</option>
            {sessions.map(s => (
              <option key={s.id} value={s.id}>
                {s.pdv_name} — {s.name} ({s.start_time?.split('T')[0]} au {s.end_time?.split('T')[0]})
              </option>
            ))}
          </select>
        </div>

        {quotaLoading && (
          <div className="text-center py-5">
            <div className="spinner-border" style={{ color: G }} role="status"></div>
          </div>
        )}

        {!quotaLoading && !quotasData && (
          <div style={{ background: CARD, borderRadius: '16px', border: `1px solid ${BORDER}`, boxShadow: SHADOW, padding: '60px', textAlign: 'center' }}>
            <i className="fas fa-calendar-plus fa-4x d-block mb-3" style={{ color: G, opacity: 0.4 }}></i>
            <p style={{ color: 'rgba(0,0,0,0.45)', fontSize: '15px' }}>Sélectionnez une session pour voir les quotas</p>
          </div>
        )}

        {!quotaLoading && quotasData && (
          <>
            <div style={{ marginBottom: '16px' }}>
              <h5 style={{ color: '#1a3322', fontWeight: 700, margin: 0 }}>
                <i className="fas fa-store me-2" style={{ color: G }}></i>{quotasData.pdv_name}
              </h5>
              <p style={{ color: 'rgba(0,0,0,0.5)', fontSize: '13px', margin: '4px 0 0' }}>
                Du {quotasData.start_date} au {quotasData.end_date}
              </p>
            </div>

            {quotasData.quotas_by_date?.map((dayData, index) => (
              <div key={index} style={{ background: CARD, borderRadius: '16px', border: `1px solid ${BORDER}`, boxShadow: SHADOW, marginBottom: '20px', overflow: 'hidden' }}>
                <div style={{ padding: '14px 20px', background: 'rgba(0,0,0,0.25)', borderBottom: `1px solid rgba(0,192,96,0.3)` }}>
                  <h6 style={{ color: '#1a3322', margin: 0, fontWeight: 700 }}>
                    <i className="fas fa-calendar-day me-2" style={{ color: G }}></i>
                    {new Date(dayData.date).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  </h6>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: 'linear-gradient(135deg, #00C060 0%, #00A651 100%)' }}>
                        {['Lot', 'Quota', 'Utilisé', 'Restant', 'Statut', 'Progression', 'Action'].map(h => (
                          <th key={h} style={{ padding: '12px 16px', color: '#1a3322', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: h === 'Lot' ? 'left' : 'center' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {dayData.quotas?.map((quota, idx) => (
                        <tr key={idx} style={{ borderBottom: `1px solid rgba(0,192,96,0.15)`, background: idx % 2 === 0 ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.1)' }}>
                          <td style={{ padding: '14px 16px' }}>
                            <div className="d-flex align-items-center gap-2">
                              {quota.color && <div style={{ width: '14px', height: '14px', borderRadius: '4px', background: quota.color, flexShrink: 0 }}></div>}
                              <div>
                                <div style={{ color: '#1a3322', fontWeight: 600, fontSize: '14px' }}>{quota.prize_label}</div>
                                {quota.prize_name && <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: '12px' }}>{quota.prize_name}</div>}
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: '14px 16px', textAlign: 'center', color: '#1a3322', fontWeight: 700 }}>{quota.max_quantity}</td>
                          <td style={{ padding: '14px 16px', textAlign: 'center', color: 'rgba(255,255,255,0.7)' }}>{quota.used_quantity}</td>
                          <td style={{ padding: '14px 16px', textAlign: 'center', fontWeight: 800, color: quota.remaining_quantity === 0 ? '#e53935' : '#00A651', fontSize: '16px' }}>
                            {quota.remaining_quantity}
                          </td>
                          <td style={{ padding: '14px 16px', textAlign: 'center' }}>{getBadge(quota.used_quantity, quota.max_quantity)}</td>
                          <td style={{ padding: '14px 16px', textAlign: 'center' }}>{getProgress(quota.used_quantity, quota.max_quantity)}</td>
                          <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                            <button onClick={() => handleEditQuota(quota)}
                              style={{ padding: '5px 12px', background: 'rgba(0,192,96,0.15)', color: '#00A651', border: `1px solid rgba(0,192,96,0.4)`, borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>
                              <i className="fas fa-edit"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </>
        )}

        {showEditModal && (
          <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.7)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content" style={{ background: '#ffffff', border: `1px solid rgba(0,192,96,0.4)`, borderRadius: '16px', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}>
                <div className="modal-header" style={{ borderBottom: `1px solid rgba(0,192,96,0.3)`, background: 'rgba(0,0,0,0.3)', borderRadius: '16px 16px 0 0' }}>
                  <h5 className="modal-title fw-bold" style={{ color: '#1a3322' }}>
                    <i className="fas fa-edit me-2" style={{ color: G }}></i>Modifier le quota
                  </h5>
                  <button type="button" className="btn-close btn-close-white" onClick={() => setShowEditModal(false)}></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label style={{ color: '#1a3322', fontWeight: 700, fontSize: '13px', display: 'block', marginBottom: '6px' }}>Date</label>
                    <input type="date" className="form-control" value={editQuota.date} disabled style={inputStyle} />
                  </div>
                  <div className="mb-3">
                    <label style={{ color: '#1a3322', fontWeight: 700, fontSize: '13px', display: 'block', marginBottom: '6px' }}>Lot</label>
                    <input type="text" className="form-control" value={editQuota.prize_label} disabled style={inputStyle} />
                  </div>
                  <div className="mb-3">
                    <label style={{ color: '#1a3322', fontWeight: 700, fontSize: '13px', display: 'block', marginBottom: '6px' }}>Quota maximum</label>
                    <input type="number" className="form-control" value={editQuota.max_quantity} min="0"
                      onChange={e => setEditQuota({ ...editQuota, max_quantity: parseInt(e.target.value) || 0 })}
                      style={inputStyle} />
                  </div>
                  <div className="mb-3">
                    <label style={{ color: '#1a3322', fontWeight: 700, fontSize: '13px', display: 'block', marginBottom: '6px' }}>Quantité utilisée</label>
                    <input type="number" className="form-control" value={editQuota.used_quantity} min="0"
                      onChange={e => setEditQuota({ ...editQuota, used_quantity: parseInt(e.target.value) || 0 })}
                      style={inputStyle} />
                  </div>
                  <div style={{ padding: '12px 16px', background: 'rgba(0,192,96,0.1)', borderRadius: '10px', border: `1px solid rgba(0,192,96,0.3)` }}>
                    <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px' }}>
                      Stock restant : <strong style={{ color: '#00A651', fontSize: '18px' }}>{Math.max(0, editQuota.max_quantity - editQuota.used_quantity)}</strong>
                    </span>
                  </div>
                </div>
                <div className="modal-footer" style={{ borderTop: `1px solid rgba(0,192,96,0.3)` }}>
                  <button className="btn fw-bold" onClick={() => setShowEditModal(false)}
                    style={{ background: 'rgba(0,0,0,0.06)', color: '#1a3322', border: '1px solid rgba(0,0,0,0.15)' }}>Annuler</button>
                  <button className="btn fw-bold" onClick={handleSaveQuota}
                    style={{ background: `linear-gradient(135deg, ${G}, #006633)`, color: '#fff', border: 'none', padding: '8px 24px', boxShadow: '0 4px 14px rgba(0,192,96,0.3)' }}>
                    <i className="fas fa-save me-2"></i>Enregistrer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DailyQuotas;
